import { configureStore } from '@reduxjs/toolkit';
import createSagaMiddleware from 'redux-saga';
import {
  authReducer,
  adminReducer,
  securitySaga,
  adminSaga,
} from '@/features/security/sagas';
import {
  clientReducer,
  clientSaga,
  agentReducer,
  agentSaga,
  propertyReducer,
  propertySaga,
  dashboardReducer,
  dashboardSaga,
  erpReducer,
  erpSaga,
} from '@/features/agency/sagas';
import { tasksSaga } from '@/features/agency/sagas/tasks.saga';
import tasksReducer from '@/features/agency/redux/tasks-slice';
import { funnelReducer, funnelSaga, leadsListReducer } from '@/features/crm/redux';
import tenantReducer from './slices/tenant-slice';
import { modulesReducer, modulesSaga } from '@/features/admin/modules/redux';
import { rbacPermissionsReducer, rbacPermissionsSaga } from '@/features/admin/modules/redux';
import plansReducer from '@/features/admin/redux/plans-slice';
import subscriptionPoliciesReducer from '@/features/admin/redux/subscription-policies-slice';
import adminBillingReducer from '@/features/admin/redux/billing-slice';
import automaticInvoicesReducer from '@/features/admin/redux/automatic-invoices-slice';
import rolesReducer from '@/features/roles/redux/roles-slice';
import tenantsReducer from '@/features/tenants/redux/tenants-slice';
import { tenantsSaga } from '@/features/tenants/redux/tenants-saga';
import residentsReducer from '@/features/condominium/redux/residents-slice';
import administrationReducer from '@/features/condominium/redux/administration-slice';
import administratorsReducer from '@/features/condominium/redux/administrators-slice';
import bulkUploadReducer from '@/features/condominium/redux/bulk-upload-slice';
import billingReducer from '@/features/condominium/redux/billing-slice';
import collaboratorsReducer from '@/features/condominium/redux/collaborators-slice';
import unitsReducer from '@/features/condominium/redux/units-slice';
import recurringInvoicesReducer from '@/features/condominium/redux/recurring-invoices-slice';
import paymentsReducer from '@/features/condominium/redux/payments-slice';
import maintenanceRequestsReducer from '@/features/condominium/redux/maintenance-requests-slice';
import condoSettingsReducer from '@/features/condominium/redux/settings-slice';
import condominiumsReducer from '@/features/condominium/redux/condominiums-slice';
import providersReducer from '@/features/condominium/redux/providers-slice';
// bulkUploadSaga removido - ahora usamos async thunks directamente
// webpage/grapejs redux removed — module now uses custom hooks (useWebpageBuilder, useWebpageList, etc.)
import quotesReducer from '@/features/agente/redux/slices/quotesSlice';
import quotesSaga from '@/features/agente/redux/sagas/quotesSaga';
import { notificationsReducer, notificationsSaga } from '@/features/notifications';
import permissionsReducer from './slices/permissions-slice';
import permissionsSaga from '../sagas/permissions-saga';
import integrationsReducer from '@/features/admin/integrations/redux/integrations-slice';
import { integrationsSaga } from '@/features/admin/integrations/redux/sagas/integrations.saga';
import chatbotReducer from '@/features/chatbot/redux/chatbot-slice';
import { chatbotSaga } from '@/features/chatbot/redux/chatbot.saga';
import { aiTextGenerationReducer, aiTextGenerationSaga } from '@/features/ai-text-generation/redux';
import trialReducer from '@/features/billing/redux/trial-slice';
import { trialSaga } from '@/features/billing/redux/trial.saga';
import settingsInvoicesReducer from '@/features/settings-shared/redux/invoices-slice';
import { invoicesSaga } from '@/features/settings-shared/redux/invoices-saga';
import impersonationReducer from '@/features/impersonation/redux/impersonation-slice';
import contactsReducer from '@/features/contacts/redux/contacts-slice';
import inboxReducer from '@/features/inbox/redux/inbox-slice';

// Crear middleware de saga
const sagaMiddleware = createSagaMiddleware();

export const store = configureStore({
  reducer: {
    auth: authReducer,
    admin: adminReducer,
    client: clientReducer,
    agent: agentReducer,
    property: propertyReducer,
    dashboard: dashboardReducer,
    tasks: tasksReducer,
    funnel: funnelReducer,
    leadsList: leadsListReducer, // LB-12 Builder B
    tenant: tenantReducer,
    modules: modulesReducer,
    rbacPermissions: rbacPermissionsReducer,
    plans: plansReducer,
    subscriptionPolicies: subscriptionPoliciesReducer,
    adminBilling: adminBillingReducer,
    automaticInvoices: automaticInvoicesReducer,
    roles: rolesReducer,
    tenants: tenantsReducer,
    residents: residentsReducer,
    administration: administrationReducer,
    administrators: administratorsReducer,
    bulkUpload: bulkUploadReducer,
    billing: billingReducer,
    collaborators: collaboratorsReducer,
    units: unitsReducer,
    recurringInvoices: recurringInvoicesReducer,
    payments: paymentsReducer,
    maintenanceRequests: maintenanceRequestsReducer,
    settings: condoSettingsReducer,
    condominiums: condominiumsReducer,
    providers: providersReducer,
    quotes: quotesReducer,
    notifications: notificationsReducer,
    permissions: permissionsReducer,
    integrations: integrationsReducer,
    chatbot: chatbotReducer,
    aiTextGeneration: aiTextGenerationReducer,
    trial: trialReducer,
    settingsInvoices: settingsInvoicesReducer,
    erp: erpReducer,
    impersonation: impersonationReducer,
    contacts: contactsReducer,
    inbox: inboxReducer,
  },
  middleware: getDefaultMiddleware =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: ['persist/PERSIST'],
        // Ignorar paths con Date objects y File objects
        ignoredActionPaths: [
          'payload.clients',
          'payload.createdAt',
          'payload.lastInteraction',
          'payload.dueDate',
          'payload.completedAt',
          'meta.arg', // Ignorar el argumento del thunk (que puede ser un File)
        ],
        ignoredPaths: [
          'client.clients',
          'agent.agents',
          'client.currentClient',
          'agent.currentAgent',
          'property.properties',
          'property.currentProperty',
          'tasks.tasks',
          'tasks.currentTask',
          'bulkUpload', // Ignorar todo el estado de bulkUpload que puede contener Files
        ],
        // Los timestamps del chatbot ahora son strings, pero por si acaso los ignoramos también
        warnAfter: 32,
      },
    }).concat(sagaMiddleware),
});

// Ejecutar sagas
sagaMiddleware.run(securitySaga);
sagaMiddleware.run(adminSaga);
sagaMiddleware.run(clientSaga);
sagaMiddleware.run(agentSaga);
sagaMiddleware.run(propertySaga);
sagaMiddleware.run(dashboardSaga);
sagaMiddleware.run(funnelSaga);
sagaMiddleware.run(quotesSaga);
sagaMiddleware.run(notificationsSaga);
sagaMiddleware.run(permissionsSaga);
sagaMiddleware.run(modulesSaga);
sagaMiddleware.run(rbacPermissionsSaga);
sagaMiddleware.run(tasksSaga);
sagaMiddleware.run(integrationsSaga);
sagaMiddleware.run(chatbotSaga);
sagaMiddleware.run(aiTextGenerationSaga);
sagaMiddleware.run(trialSaga);
sagaMiddleware.run(tenantsSaga);
sagaMiddleware.run(invoicesSaga);
sagaMiddleware.run(erpSaga);
// bulkUploadSaga removido - ahora usamos async thunks directamente

export type RootState = ReturnType<typeof store.getState>;
export type AppDispatch = typeof store.dispatch;
